<template>
  <div>
    <div class="tw-overflow-x-auto">
      <table class="tw-min-w-full tw-text-left tw-text-sm tw-text-slate-700">
        <thead class="tw-bg-slate-50/70 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">
          <tr>
            <th class="tw-px-4 tw-py-3">Order Id</th>
            <th class="tw-px-4 tw-py-3" v-if="!appointmentsView">Scheduled Date</th>
            <th class="tw-px-4 tw-py-3" v-else-if="terminalAppt">Terminal Appointment</th>
            <th class="tw-px-4 tw-py-3" v-else>Delivery Appointment</th>
            <th class="tw-px-4 tw-py-3">Driver</th>
            <th class="tw-px-4 tw-py-3">Container</th>
            <th class="tw-px-4 tw-py-3">Chassis</th>
            <th class="tw-px-4 tw-py-3">Size/Type</th>
            <th class="tw-px-4 tw-py-3">Origin</th>
            <th class="tw-px-4 tw-py-3">Destination</th>
            <th class="tw-px-4 tw-py-3">Move</th>
            <th class="tw-px-4 tw-py-3">Receiver</th>
            <th class="tw-px-4 tw-py-3"></th>
          </tr>
        </thead>
        <tbody class="tw-divide-y tw-divide-slate-200/70">
          <tr
            v-for="(trip, containerIdx) in mergedTrips"
            :key="`${containerIdx}-${trip && trip.id ? trip.id : 'trip'}`"
            class="tw-cursor-pointer hover:tw-bg-slate-900/5"
            @click="selectTrip(trip)"
          >
            <td class="tw-px-4 tw-py-3 tw-font-medium tw-text-slate-900 tw-whitespace-nowrap">{{ trip.container.order.id }}</td>
          <td v-if="deliveryAppt" class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
            <p class="uk-margin-remove-bottom">
              {{
                formatAppointmentDatetime(
                  trip.container.deliveryAppointmentDate,
                  trip.container.deliveryAppointmentHasTime,
                  (showFullYear = true),
                ).split('@')[0]
              }}
            </p>
            <p class="uk-margin-remove-top">
              {{
                formatAppointmentDatetime(
                  trip.container.deliveryAppointmentDate,
                  trip.container.deliveryAppointmentHasTime,
                  (showFullYear = true),
                ).split('@')[1]
              }}
            </p>
          </td>
          <td v-else-if="terminalAppt" class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
            <p class="uk-margin-remove-bottom">
              {{
                formatAppointmentDatetime(
                  trip.container.terminalAppointmentDate,
                  trip.container.terminalAppointmentHasTime,
                  (showFullYear = true),
                ).split('@')[0]
              }}
            </p>
            <p class="uk-margin-remove-top">
              {{
                formatAppointmentDatetime(
                  trip.container.terminalAppointmentDate,
                  trip.container.terminalAppointmentHasTime,
                  (showFullYear = true),
                ).split('@')[1]
              }}
            </p>
          </td>
          <td v-else-if="editingTrip.id == trip.id" class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
            <DatePicker
              class="uk-form-label"
              :input-class="'uk-input uk-width-small'"
              format="MM/DD/YYYY"
              v-model="trip.scheduledDate"
            ></DatePicker>
          </td>
          <td v-else class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
            {{ trip.scheduledDate ? formatDate(trip.scheduledDate) : '--' }}
          </td>
          <td v-if="editingTrip.id === trip.id" class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
            <FilterableDropdown
              v-model="editingTrip.driver"
              :options="drivers"
              class="uk-padding-remove-left uk-padding-remove-right uk-width-medium"
              label="fullName"
              :components="{ OpenIndicator: null, Deselect: null }"
            ></FilterableDropdown>
          </td>
          <td v-else class="tw-px-4 tw-py-3 tw-whitespace-nowrap">{{ trip.driver ? trip.driver.fullName : '' }}</td>
          <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
            {{
              trip.container.containerId !== 'EXPUNDEF'
                ? trip.container.containerId
                : 'Container Undefined'
            }}
            <HourGlass
              v-if="trip.container.terminalAppointmentDate"
              uk-tooltip="Container has Terminal Appointment"
            />
            <EventSchedule
              v-if="trip.container.deliveryAppointmentDate"
              uk-tooltip="Container has Delivery Appointment"
            />
          </td>
          <td v-if="editingTrip.id === trip.id" class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
            <input
              type="text"
              v-model="editingTrip.chassis"
              class="uk-input uk-form-small uk-form-width-small"
              placeholder="Chassis"
            />
          </td>
          <td v-else class="tw-px-4 tw-py-3 tw-whitespace-nowrap">{{ trip.chassis }}</td>
          <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">{{ trip.container.size }}/{{ trip.container.type }}</td>
          <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">{{ addressName(trip.origin) }}</td>
          <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">{{ addressName(trip.destination) }}</td>
          <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
            <TripTypeField
              :showLabel="false"
              :trip="trip"
              :isEditing="editingTrip.id === trip.id"
              :darkGreen="trip.status == 'Complete'"
              :gray="trip.status != 'Complete' && trip.status != 'In Progress'"
              :green="trip.status == 'In Progress'"
            ></TripTypeField>
          </td>
          <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
            {{ customerText(trip) }}
          </td>
          <td class="tw-px-4 tw-py-3 tw-text-right" @click.stop>
            <Button
              v-if="
                trip.status != 'In Progress' &&
                trip.status != 'Complete' &&
                editingTrip.id !== trip.id
              "
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-emerald-600 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-emerald-700"
              :disabled="loadingTripId === trip.id"
              @click="startTrip(trip)"
            >
              <div
                v-if="loadingTripId === trip.id"
                class="uk-margin-right"
                uk-spinner
              ></div>
              {{ loadingTripId === trip.id ? 'Starting...' : 'Start Trip' }}
            </Button>
            <button
              v-else-if="
                trip.status == 'In Progress' && editingTrip.id !== trip.id
              "
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-slate-900 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800"
              :disabled="loadingTripId === trip.id"
              @click="completeTrip(trip)"
            >
              <div
                v-if="loadingTripId === trip.id"
                class="uk-margin-right"
                uk-spinner
              ></div>
              {{ loadingTripId === trip.id ? 'Completing...' : 'Complete Trip' }}
            </button>
            <!-- <button v-else-if="trip.status == 'In Progress' && (tripIdx == 0 || container.trips[tripIdx-1].status == 'Complete')" @click="completeTrip(trip)" disabled uk-tooltip="Trips for this order are not complete. Please check order status."> Complete Trip</button> -->
            <button
              v-if="editingTrip.id === trip.id"
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-slate-900 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800"
              @click="saveTrip(trip)"
            >
              Save Trip
            </button>
            <button
              v-if="editingTrip.id === trip.id"
              class="tw-ml-2 tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
              @click="cancelEditTrip"
            >
              Cancel
            </button>
            <div v-else @click.stop>
              <button
                class="tw-ml-2 tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-text-slate-700 hover:tw-bg-white"
                type="button"
                aria-label="More"
                title="More"
              >
                <span uk-icon="icon:  cog"></span>
              </button>
              <div uk-dropdown="mode: click; duration: 0">
                <ul class="uk-nav uk-dropdown-nav">
                  <li @click="editTrip(trip)">
                    <a>Edit Trip</a>
                  </li>
                  <li class="uk">
                    <a
                      :href="
                        printDispatchURL(
                          trip.container.id,
                          trip.id,
                          trip.container.order.id,
                        )
                      "
                      target="_blank"
                      >Print Dispatch Sheet</a
                    >
                  </li>
                  <li
                    v-if="
                      trip.status != 'Complete' && trip.status == 'In Progress'
                    "
                    @click="completeTrip(trip)"
                  >
                    <a>Complete Trip</a>
                  </li>
                  <li
                    v-if="trip.status == TRIP_STATUS.NOT_STARTED"
                    @click="startTrip(trip)"
                  >
                    <a>Start Trip</a>
                  </li>
                  <li
                    v-if="
                      trip.status == TRIP_STATUS.IN_PROGRESS ||
                      trip.status === TRIP_STATUS.COMPLETE
                    "
                    @click="revertTrip(trip)"
                  >
                    <a>Revert Trip</a>
                  </li>
                </ul>
              </div>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import EditTripStatus from '@/gql/orders/trips/mutations/EditTripStatus.gql';
import DatePicker from '@/components/core/DateTime/DatePicker.vue';
import HourGlass from '@carbon/icons-vue/es/hourglass/24';
import EventSchedule from '@carbon/icons-vue/es/event--schedule/24';

import FilterableDropdown from '../core/FilterableDropdown.vue';
import EditTripMutation from '../../gql/dispatch/EditTrip.gql';

import Button from '@/components/core/Button.vue';
import { pushToast } from '@/lib/toast';

import TripTypeField from '../Orders/UI/TripTypeField.vue';
import { TRIP_STATUS } from '../../types';
import { dateFormatMixin, datetimeFormatMixin } from '../../utils/date';

export default defineComponent({
  components: {
    FilterableDropdown,
    TripTypeField,
    DatePicker,
    HourGlass,
    EventSchedule,
    Button
  },

  props: {
    trips: { required: true },
    tripsCompleted: {
      default: () => [],
    },
    drivers: { required: true },
    appointmentsView: {
      default: false,
    },
    deliveryAppt: {
      default: false,
    },
    terminalAppt: {
      default: false,
    },
  },
  mixins: [dateFormatMixin, datetimeFormatMixin],
  data(): any {
    return {
      selectedTrip: -1,
      loadingTripId: -1,
      searchTerm: '',
      editingTrip: {
        id: undefined,
        driver: undefined,
        chassis: undefined,
        isEditingDriver: false,
        isEditingChassis: false,
      },
      TRIP_STATUS,
    };
  },
  computed: {
    mergedTrips(): any[] {
      const vm = (this as any);
      const trips = Array.isArray(vm.trips) ? vm.trips : [];
      const tripsCompleted = Array.isArray(vm.tripsCompleted) ? vm.tripsCompleted : [];
      return trips.concat(tripsCompleted);
    },
  },
  methods: {
    selectTrip(trip) {
      const vm = (this as any);
      
      if (vm.editingTrip.id !== trip.id) {
        vm.$emit('selectTrip', trip.container.id);
      }
    },
    customerText(trip): string {
      return `${trip.container.order.receiver.name}`;
    },
    addressName(address: any) {
      if (!address) return '';
      if (address.shortName) return address.shortName;
      else if (address.name) return address.name;

      return '';
    },
    cancelEditTrip() {
      (this as any).editingTrip.id = -1;
    },
    changedScheduledDispatch(dateStr: string, trip) {
      const parsedDate = (this as any).formatDate(dateStr);
    },
    printDispatchURL(containerId, tripId, orderId) {
      return `/print/order/dispatch/${orderId}/container/${containerId}/trip/${tripId}`;
    },
    editTrip(trip) {
      const vm = (this as any);

      vm.editingTrip.driver = trip.driver;
      vm.editingTrip.id = trip.id;
      vm.editingTrip.chassis = trip.chassis;
    },
    startTrip(trip) {
      const vm = this as any;
      if (vm.loadingTripId === trip.id) return;
      vm.loadingTripId = trip.id;
      vm.$apollo
        .mutate({
          mutation: EditTripStatus,
          variables: {
            tripId: Number(trip.id),
            status: 'In Progress',
          },
        })
        .then(() => {
          this.updateDispatchContainers();
        })
        .catch(() => {
          pushToast('Unable to start the trip. Please try again.', 'error');
        })
        .finally(() => {
          vm.loadingTripId = -1;
        });
    },
    completeTrip(trip) {
      const vm = this as any;
      if (vm.loadingTripId === trip.id) return;
      vm.loadingTripId = trip.id;
      vm.$apollo
        .mutate({
          mutation: EditTripStatus,
          variables: {
            tripId: Number(trip.id),
            status: 'Complete',
          },
        })
        .then(() => {
          // (this as any).$emit('refreshOrders');
          this.updateDispatchContainers();
        })
        .catch(() => {
          pushToast('Unable to complete the trip. Please try again.', 'error');
        })
        .finally(() => {
          vm.loadingTripId = -1;
        });
    },

    revertTrip(trip) {
      (this as any).$apollo
        .mutate({
          mutation: EditTripStatus,
          variables: {
            tripId: Number(trip.id),
            status: TRIP_STATUS.NOT_STARTED,
          },
        })
        .then(() => {
          this.updateDispatchContainers();
        });
    },

    updateDispatchContainers() {
      const vm = (this as any);

      vm.$emit('updateContainers');
    },

    saveTrip(trip) {
      const vm = (this as any);

      const chassis = vm.editingTrip.chassis || '';
      vm.$apollo
        .mutate({
          mutation: EditTripMutation,
          variables: {
            tripId: Number(trip.id),
            tripType: trip.tripType,
            originId: trip.origin.id,
            destinationId: trip.destination.id,
            driverId: vm.editingTrip.driver?.id,
            scheduledDate: trip.scheduledDate,
            chassis,
          },
        })
        .then(() => {
          vm.editingTrip.id = undefined;
        })
        .catch((err) => {
          vm.editingTrip.id = undefined;
        });

      // Send mutation
      console.log(`saved trip data ${JSON.stringify(trip)}`);
    },
  },
});
</script>

<style scoped>
</style>
